import React from "react";

/**
 * DANY — the assistant's face. The ONE place Dany's identity renders.
 *
 * Dany is NOT ZIPPY. ZIPPY is the brand logo: one fixed pose, no expressions,
 * and animating it devalues the mark. Dany is the character Daniel drew — two
 * oversized donut eyes in a lobed body with antennae — coloured into the brand:
 * `--hive` body, `--worker` outline. The yellow is what makes it read as a bee
 * rather than a generic bug; no stripes or wings are needed, and both would
 * disappear at 24px anyway.
 *
 * The eyes are TWO concentric pieces, which is the whole point: the ring holds
 * the shape while the pupil moves and changes colour independently. Every state
 * below is the same drawing with a different pupil.
 *
 *   idle       pupils centred, slow blink, body breathing
 *   listening  pupils wide and dilating, tips bright yellow — the client's turn
 *   thinking   gaze parked up-left and held, blinking — Dany is reasoning
 *   working    gaze down, sweeping side to side like reading — a tool call
 *   speaking   pupils become two dusk level bars, out of phase — Dany's turn
 *   happy      pupils become arches — a result landed
 *   error      pupils red, one shake
 *
 * Turn-taking is carried by colour, same rule as DanyVoice: yellow = you,
 * dusk = Dany. The bright brand yellow lives on the antenna tips (outlined, so
 * it stays visible); the pupils use `--hive-ink`, because a pupil is a small
 * mark on a light ring and the system's rule is gold fills, ink writes.
 */
const TONE = {
  idle:      { pupil: "var(--worker)",   tip: "var(--worker)" },
  listening: { pupil: "var(--hive-ink)", tip: "var(--hive)" },
  thinking:  { pupil: "var(--worker)",   tip: "var(--sand-deep)" },
  working:   { pupil: "var(--dusk)",     tip: "var(--dusk)" },
  speaking:  { pupil: "var(--dusk)",     tip: "var(--dusk)" },
  happy:     { pupil: "var(--success)",  tip: "var(--success)" },
  error:     { pupil: "var(--danger)",   tip: "var(--danger)" },
};

/* Union of two r18 circles at (20,38) and (44,38) — one path, so the outline
   never crosses the middle of the face. */
const BODY = "M32 24.58A18 18 0 1 0 32 51.42A18 18 0 1 0 32 24.58Z";
const EYES = [20, 44];

export function DanyAvatar({ size = 32, state = "idle", src, halo = true, title, style = {} }) {
  const tone = TONE[state] || TONE.idle;
  const art = src && (typeof src === "string" ? src : src[state] || src.idle);

  if (art) {
    return (
      <span style={{ position: "relative", display: "inline-flex", flex: "0 0 auto", ...style }}>
        {halo && <span style={{ position: "absolute", inset: -Math.round(size * 0.26), background: "var(--dany-halo)", borderRadius: "var(--radius-pill)" }} />}
        <img src={art} alt="Dany" width={size} height={size} draggable={false} style={{ position: "relative", objectFit: "contain" }} />
      </span>
    );
  }

  const bodyAnim =
    state === "thinking" ? "fixy-dany-tilt 2.6s var(--ease-in-out) infinite"
    : state === "error" ? "fixy-dany-shake .45s var(--ease-out) 1"
    : state === "listening" ? "fixy-dany-breathe 1.6s var(--ease-in-out) infinite"
    : state === "working" ? "fixy-dany-breathe 1.9s var(--ease-in-out) infinite"
    : "fixy-dany-breathe 4.2s var(--ease-in-out) infinite";

  const pupilAnim =
    state === "listening" ? "fixy-dany-dilate 1.6s var(--ease-in-out) infinite"
    : state === "thinking" ? "fixy-dany-ponder 3.4s var(--ease-in-out) infinite"
    : state === "working" ? "fixy-dany-read 1.5s var(--ease-in-out) infinite"
    : "none";

  const tipAnim = (state === "listening" || state === "working")
    ? "fixy-dany-pip 1.1s var(--ease-in-out) infinite" : "none";

  return (
    <span style={{ position: "relative", display: "inline-flex", flex: "0 0 auto", ...style }}>
      {halo && <span style={{ position: "absolute", inset: -Math.round(size * 0.26), background: "var(--dany-halo)", borderRadius: "var(--radius-pill)" }} />}
      <svg width={size} height={size} viewBox="0 0 64 64" role="img"
        aria-label={title || `Dany — ${state}`} style={{ position: "relative", overflow: "visible" }}>
        <g style={{ transformOrigin: "32px 36px", animation: bodyAnim }}>
          {/* antennae — drawn first so the body covers their roots */}
          <path d="M27 27 14 10M37 27 50 10" stroke="var(--worker)" strokeWidth="4.2" strokeLinecap="round" fill="none" />
          {[12.5, 51.5].map((cx) => (
            <circle key={cx} cx={cx} cy="7" r="5.4" fill={tone.tip} stroke="var(--worker)" strokeWidth="3"
              style={{ transformOrigin: `${cx}px 7px`, animation: tipAnim }} />
          ))}

          <path d={BODY} fill="var(--hive)" stroke="var(--worker)" strokeWidth="3.2" strokeLinejoin="round" />

          <g style={{ transformOrigin: "32px 38px", animation: (state === "idle" || state === "thinking") ? "fixy-dany-blink 5.5s var(--ease-in-out) infinite" : "none" }}>
            {EYES.map((cx) => (
              <circle key={cx} cx={cx} cy="38" r="11" fill="var(--paper)" stroke="var(--worker)" strokeWidth="2.6" />
            ))}
            {state === "happy"
              ? EYES.map((cx) => (
                  <path key={cx} d={`M${cx - 5.6} 40.5Q${cx} 32.5 ${cx + 5.6} 40.5`}
                    fill="none" stroke={tone.pupil} strokeWidth="4.2" strokeLinecap="round" />
                ))
              : state === "speaking"
              ? EYES.map((cx, i) => (
                  <rect key={cx} x={cx - 2.9} y="30.5" width="5.8" height="15" rx="2.9" fill={tone.pupil}
                    style={{ transformOrigin: `${cx}px 38px`, animation: `fixy-dany-vu-${i ? "b" : "a"} ${i ? ".47" : ".56"}s var(--ease-in-out) infinite` }} />
                ))
              : EYES.map((cx) => (
                  <circle key={cx} cx={cx} cy="38" r={state === "listening" ? 5.6 : 5}
                    fill={tone.pupil}
                    style={{ transformOrigin: `${cx}px 38px`, animation: pupilAnim }} />
                ))}
          </g>
        </g>
      </svg>
    </span>
  );
}
